import React, { useMemo } from "react";
import { SbSpin, SbEmpty } from "uiCore";
import { PAIC_VIEW_MODES } from "constants/peoplePlusAI.constants";
import CoreMarkdownEditor from "Components/SbMarkdownEditor/CoreMarkdownEditor";
import MermaidBlock from "Components/SbMarkdownEditor/MermaidBlock";
import {
  LoadingShell,
  ViewerShell,
} from "Pages/PeoplePlusAIV2/shared/DocumentView.styles";

// ─── Mermaid block splitting ─────────────────────────────────────────────────
// Reuses the same regex pattern as useMermaidPdfImages so UI and PDF
// handle mermaid blocks identically.
const MERMAID_FENCE_RE = /```mermaid\s*\n([\s\S]*?)```/g;

/**
 * Splits markdown into ordered segments of plain-text and mermaid code.
 *
 * @param {string} markdown  Raw markdown that may contain mermaid blocks.
 * @returns {Array<{type: "markdown"|"mermaid", content: string}>}
 */
const splitMarkdownSegments = (markdown) => {
  if (!markdown) return [{ type: "markdown", content: "" }];

  const segments = [];
  const re = new RegExp(MERMAID_FENCE_RE.source, MERMAID_FENCE_RE.flags);
  let lastIndex = 0;
  let match;

  while ((match = re.exec(markdown)) !== null) {
    const textBefore = markdown.slice(lastIndex, match.index);
    if (textBefore) {
      segments.push({ type: "markdown", content: textBefore });
    }
    segments.push({ type: "mermaid", content: match[1] });
    lastIndex = re.lastIndex;
  }

  const textAfter = markdown.slice(lastIndex);
  if (textAfter) {
    segments.push({ type: "markdown", content: textAfter });
  }

  return segments.length > 0
    ? segments
    : [{ type: "markdown", content: markdown }];
};

/**
 * Shared document content component for all PAIC document views.
 *
 * Mermaid fenced code blocks are extracted from the markdown and rendered
 * as interactive diagrams via MermaidBlock.  All remaining markdown is
 * rendered through CoreMarkdownEditor (MDXEditor) in read-only mode.
 *
 * Replaces: PACJdPrdContentV2, PACJdViewContentV2, PACPeopleJDContentV2
 */
const SbPACDocumentContentV2 = ({
  loading,
  content,
  contentKey,
  emptyText = "No content available",
}) => {
  // Split once per content change — avoids re-parsing on every render.
  const segments = useMemo(() => splitMarkdownSegments(content), [content]);

  if (loading) {
    return (
      <LoadingShell>
        <SbSpin size="large" />
      </LoadingShell>
    );
  }

  if (!content) {
    return (
      <LoadingShell>
        <SbEmpty description={emptyText} />
      </LoadingShell>
    );
  }

  // If there are no mermaid blocks, render the simple single-editor path.
  const hasMermaid = segments.some((s) => s.type === "mermaid");

  if (!hasMermaid) {
    return (
      <ViewerShell>
        <CoreMarkdownEditor
          key={contentKey}
          initialMarkdown={content}
          viewMode={PAIC_VIEW_MODES.VIEW}
          className="sb-markdown-editor sb-markdown-editor--segmented"
        />
      </ViewerShell>
    );
  }

  // Render each segment in order: markdown via editor, mermaid via MermaidBlock.
  return (
    <ViewerShell>
      {segments.map((segment, idx) => {
        if (segment.type === "mermaid") {
          return (
            <MermaidBlock
              key={`${contentKey}-mermaid-${idx}`}
              code={segment.content}
            />
          );
        }

        // Skip empty markdown segments.
        if (!segment.content?.trim()) return null;

        return (
          <CoreMarkdownEditor
            key={`${contentKey}-md-${idx}`}
            initialMarkdown={segment.content}
            viewMode={PAIC_VIEW_MODES.VIEW}
            className="sb-markdown-editor sb-markdown-editor--segmented"
          />
        );
      })}
    </ViewerShell>
  );
};

export default SbPACDocumentContentV2;
